import React, { useEffect, useMemo, useRef, useState } from "react";
import ScheduleGroupSelector from "./ScheduleGroupSelector";
import ScheduleVisitOptionalSections from "./ScheduleVisitOptionalSections";
import {
  MiniField,
  NumberBox,
  SignedNumberBox,
  StaticSetupSection,
} from "./scheduleVisitPanelUi";
import {
  VISIT_TYPE,
  buildReferenceVisitOptions,
  calcLegacyPeriodDuration,
  calcScreeningDuration,
  canDeleteVisitFromList,
  cohortsForGroupDropdown,
  createVisitDraft,
  findDuplicateVisitName,
  getReorderableVisits,
  isAdhocVisit,
  nextDefaultVisitName,
  resolveScheduleGroupId,
  visitNameUnchanged,
  visitsForVisitRail,
  visitsWithoutAdhoc,
} from "./scheduleVisitDraft";
import {
  getResidentialRangeError,
  getScreeningRangeError,
  validateVisitReferenceForSave,
  visitHasPersistableDetails,
} from "./scheduleVisitPersistence";

function isBlank(value) {
  return String(value ?? "").trim() === "";
}

function LegacyField({ label, required, compact, children }) {
  return (
    <label className={`gvisit-legacy-field${compact ? " gvisit-legacy-field--compact" : ""}`}>
      <span className="gvisit-legacy-field__label">
        {label}
        {required ? <span className="gvisit-legacy-field__req">*</span> : null}
      </span>
      {children}
    </label>
  );
}

function sameVisitId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function VisitRailItem({
  visit,
  index,
  selected,
  canRemove,
  canDrag,
  canDrop,
  isDragging,
  isDropTarget,
  onSelect,
  onRequestRemove,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onRenameStart,
  renaming,
  renameValue,
  onRenameValueChange,
  onRenameCommit,
  onRenameCancel,
  structureBusy,
  lockVisitIdentity = false,
}) {
  const isScreening = visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening;
  const isAdhoc = isAdhocVisit(visit);
  const typeClass = isAdhoc
    ? "is-adhoc"
    : isScreening
      ? "is-screening"
      : visit.visitType === VISIT_TYPE.RESIDENTIAL
        ? "is-res"
        : "is-non-res";

  if (renaming) {
    return (
      <div
        className={`gvisit-rail-item is-renaming ${typeClass}${
          selected ? " is-selected" : ""
        }`}
      >
        <input
          type="text"
          className="gvisit-rail-item__rename-input"
          value={renameValue}
          maxLength={80}
          autoFocus
          onChange={(e) => onRenameValueChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onRenameCommit?.();
            if (e.key === "Escape") onRenameCancel?.();
          }}
        />
        <div className="gvisit-rail-item__actions">
          <button
            type="button"
            className="gvisit-rail-item__action gvisit-rail-item__action--save"
            aria-label="Save visit name"
            onClick={onRenameCommit}
          >
            <i className="fas fa-check" aria-hidden />
          </button>
          <button
            type="button"
            className="gvisit-rail-item__action"
            aria-label="Cancel rename"
            onClick={onRenameCancel}
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`gvisit-rail-item${selected ? " is-selected" : ""} ${typeClass}${
        isDragging ? " is-dragging" : ""
      }${isDropTarget ? " is-drop-target" : ""}`}
      role="option"
      aria-selected={selected}
      onDragOver={canDrop ? onDragOver : undefined}
      onDragLeave={canDrop ? onDragLeave : undefined}
      onDrop={canDrop ? onDrop : undefined}
    >
      {canDrag ? (
        <button
          type="button"
          className="gvisit-rail-item__drag"
          aria-label={`Drag to reorder ${visit.visitName}`}
          title="Drag to reorder"
          disabled={structureBusy}
          draggable={!structureBusy}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <i className="fas fa-grip-vertical" aria-hidden />
        </button>
      ) : (
        <span className="gvisit-rail-item__drag gvisit-rail-item__drag--spacer" aria-hidden />
      )}
      <button
        type="button"
        className="gvisit-rail-item__select"
        aria-current={selected ? "true" : undefined}
        onClick={onSelect}
      >
        <span className="gvisit-rail-item__badge">{isAdhoc ? "A" : index + 1}</span>
        <span className="gvisit-rail-item__name">{visit.visitName}</span>
      </button>
      {!isAdhoc ? (
        <div className="gvisit-rail-item__actions">
          {!isScreening ? (
            <button
              type="button"
              className="gvisit-rail-item__action"
              aria-label={`Rename ${visit.visitName}`}
              disabled={structureBusy || lockVisitIdentity}
              onClick={(e) => {
                e.stopPropagation();
                onRenameStart?.();
              }}
            >
              <i className="fas fa-pencil-alt" aria-hidden />
            </button>
          ) : null}
          {!isScreening ? (
            <button
              type="button"
              className="gvisit-rail-item__action gvisit-rail-item__action--danger"
              disabled={!canRemove || structureBusy || lockVisitIdentity}
              aria-label={`Delete ${visit.visitName}`}
              title={
                lockVisitIdentity
                  ? "This schedule is live. Visits cannot be deleted."
                  : canRemove
                    ? "Delete visit"
                    : "At least one regular visit is required"
              }
              onClick={(e) => {
                e.stopPropagation();
                if (canRemove) onRequestRemove?.();
              }}
            >
              <i className="fas fa-trash-alt" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function VisitRailList({
  visits,
  persistableVisits,
  studyScreening,
  selectedVisitId,
  renamingVisitId,
  renameValue,
  structureBusy,
  onRenameValueChange,
  onRenameStart,
  onRenameCommit,
  onRenameCancel,
  onSelect,
  onRequestRemove,
  onReorder,
  lockVisitIdentity = false,
}) {
  const [dragVisitId, setDragVisitId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const listInnerRef = useRef(null);

  useEffect(() => {
    if (!selectedVisitId || !listInnerRef.current) return;
    const selectedEl = listInnerRef.current.querySelector(".gvisit-rail-item.is-selected");
    selectedEl?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedVisitId, visits.length]);

  const clearDrag = () => {
    setDragVisitId(null);
    setDropTargetId(null);
  };

  const handleDragStart = (event, visit) => {
    if (structureBusy || renamingVisitId) {
      event.preventDefault();
      return;
    }
    setDragVisitId(visit.id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", visit.id);
  };

  const handleDragOver = (event, visit) => {
    if (!dragVisitId || dragVisitId === visit.id) return;
    if (isAdhocVisit(visit) || visit.isScreening || visit.visitType === VISIT_TYPE.SCREENING) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDropTargetId(visit.id);
  };

  const handleDrop = (event, targetVisit) => {
    event.preventDefault();
    if (!dragVisitId || dragVisitId === targetVisit.id) {
      clearDrag();
      return;
    }
    if (
      isAdhocVisit(targetVisit) ||
      targetVisit.isScreening ||
      targetVisit.visitType === VISIT_TYPE.SCREENING
    ) {
      clearDrag();
      return;
    }
    const reorderable = getReorderableVisits(visits);
    const fromIndex = reorderable.findIndex((v) => v.id === dragVisitId);
    const toIndex = reorderable.findIndex((v) => v.id === targetVisit.id);
    if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
      onReorder?.({
        visit: reorderable[fromIndex],
        fromIndex,
        toIndex,
      });
    }
    clearDrag();
  };

  if (!visits.length) {
    return (
      <p className="gvisit-rail--empty">No visits yet. Use + Add visit to create one.</p>
    );
  }

  return (
    <div
      ref={listInnerRef}
      className="gvisit-rail__list-inner"
      role="listbox"
      aria-label="Visits"
    >
      {visits.map((visit, index) => {
        const persistableIndex = persistableVisits.findIndex((v) => v.id === visit.id);
        const isScreening = visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening;
        const canDrag =
          !structureBusy &&
          !lockVisitIdentity &&
          !renamingVisitId &&
          !isScreening &&
          !isAdhocVisit(visit) &&
          !!visit.visitsPerStudyId;
        const canDrop = !isScreening && !isAdhocVisit(visit);

        return (
          <VisitRailItem
            key={visit.id}
            visit={visit}
            index={persistableIndex >= 0 ? persistableIndex : index}
            selected={sameVisitId(visit.id, selectedVisitId)}
            canRemove={
              !lockVisitIdentity &&
              canDeleteVisitFromList(visit, persistableVisits, studyScreening)
            }
            canDrag={canDrag}
            canDrop={canDrop && !lockVisitIdentity}
            isDragging={dragVisitId === visit.id}
            isDropTarget={dropTargetId === visit.id}
            structureBusy={structureBusy}
            lockVisitIdentity={lockVisitIdentity}
            renaming={renamingVisitId === visit.id}
            renameValue={renameValue}
            onRenameValueChange={onRenameValueChange}
            onRenameStart={() => onRenameStart?.(visit)}
            onRenameCommit={onRenameCommit}
            onRenameCancel={onRenameCancel}
            onSelect={() => onSelect?.(visit)}
            onRequestRemove={() => onRequestRemove?.(visit)}
            onDragStart={(event) => handleDragStart(event, visit)}
            onDragOver={(event) => handleDragOver(event, visit)}
            onDragLeave={() => {
              if (dropTargetId === visit.id) setDropTargetId(null);
            }}
            onDrop={(event) => handleDrop(event, visit)}
            onDragEnd={clearDrag}
          />
        );
      })}
    </div>
  );
}

function ScreeningDetailForm({
  visit,
  onChange,
  onSave,
  saving,
  onNotify,
  saveLabel = "Save",
  readOnly = false,
}) {
  const [rangeError, setRangeError] = useState("");
  const [invalidField, setInvalidField] = useState("");

  const recomputeDuration = (start, finish) =>
    calcScreeningDuration(start, finish);

  const setField = (field, value) => {
    if (readOnly) return;
    const next = { ...visit, [field]: value };
    if (field === "screeningWindowStart" || field === "screeningWindowFinish") {
      const start =
        field === "screeningWindowStart" ? value : visit.screeningWindowStart;
      const finish =
        field === "screeningWindowFinish" ? value : visit.screeningWindowFinish;
      next.screeningDuration = recomputeDuration(start, finish);
    }
    onChange(next);
  };

  const handleBlur = (field) => {
    if (readOnly) return;
    const start = visit.screeningWindowStart;
    const finish = visit.screeningWindowFinish;
    if (isBlank(start) || isBlank(finish)) return;

    const error = getScreeningRangeError(start, finish, { compareOnly: true });
    if (!error) {
      setRangeError("");
      setInvalidField("");
      return;
    }
    setRangeError(error);
    setInvalidField(field);
    onNotify?.({ message: error, type: "warning" });
    const next = { ...visit, screeningDuration: "" };
    if (field === "screeningWindowFinish") {
      next.screeningWindowFinish = "";
    } else {
      next.screeningWindowStart = "";
    }
    onChange(next);
  };

  const handleSave = () => {
    const error = getScreeningRangeError(
      visit.screeningWindowStart,
      visit.screeningWindowFinish,
      { requireBoth: true },
    );
    if (error) {
      setRangeError(error);
      setInvalidField("screeningWindowFinish");
      onNotify?.({ message: error, type: "warning" });
      return;
    }
    onSave?.();
  };

  return (
    <StaticSetupSection
      title="Visit time point"
      onSave={readOnly ? null : handleSave}
      saving={saving}
      saveLabel={saveLabel}
      readOnly={readOnly}
    >
      <div className="gvisit-mini-grid gvisit-mini-grid--3">
        <MiniField
          label="Start"
          required
          error={invalidField === "screeningWindowStart" ? rangeError : ""}
        >
          <NumberBox
            value={visit.screeningWindowStart}
            allowNegative
            maxLength={6}
            invalid={invalidField === "screeningWindowStart"}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={(v) => setField("screeningWindowStart", v)}
            onBlur={() => handleBlur("screeningWindowStart")}
          />
        </MiniField>
        <MiniField
          label="Finish"
          required
          error={invalidField === "screeningWindowFinish" ? rangeError : ""}
        >
          <NumberBox
            value={visit.screeningWindowFinish}
            allowNegative
            maxLength={6}
            invalid={invalidField === "screeningWindowFinish"}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={(v) => setField("screeningWindowFinish", v)}
            onBlur={() => handleBlur("screeningWindowFinish")}
          />
        </MiniField>
        <MiniField label="Duration" required>
          <NumberBox value={visit.screeningDuration} readOnly maxLength={3} />
        </MiniField>
      </div>
    </StaticSetupSection>
  );
}

function ResidentialDetailForm({ visit, onChange, onNotify, readOnly = false }) {
  const [rangeError, setRangeError] = useState("");
  const [invalidField, setInvalidField] = useState("");

  const setField = (field, value) => {
    if (readOnly) return;
    const next = { ...visit, [field]: value };
    if (field === "startPeriod" || field === "finishPeriod") {
      const start = field === "startPeriod" ? value : visit.startPeriod;
      const finish = field === "finishPeriod" ? value : visit.finishPeriod;
      next.periodDuration = calcLegacyPeriodDuration(start, finish);
    }
    onChange(next);
  };

  const handleBlur = () => {
    if (readOnly) return;
    if (isBlank(visit.startPeriod) || isBlank(visit.finishPeriod)) return;

    const error = getResidentialRangeError(visit.startPeriod, visit.finishPeriod);
    if (!error) {
      setRangeError("");
      setInvalidField("");
      return;
    }
    setRangeError(error);
    setInvalidField("finishPeriod");
    onNotify?.({ message: error, type: "warning" });
    const next = { ...visit, periodDuration: "", finishPeriod: "" };
    onChange(next);
  };

  return (
    <>
      <div className="gvisit-mini-grid gvisit-mini-grid--3">
        <MiniField
          label="Start"
          required
          error={invalidField === "startPeriod" ? rangeError : ""}
        >
          <NumberBox
            value={visit.startPeriod}
            maxLength={3}
            invalid={invalidField === "startPeriod"}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={(v) => setField("startPeriod", v)}
            onBlur={handleBlur}
          />
        </MiniField>
        <MiniField
          label="Finish"
          required
          error={invalidField === "finishPeriod" ? rangeError : ""}
        >
          <NumberBox
            value={visit.finishPeriod}
            maxLength={3}
            invalid={invalidField === "finishPeriod"}
            readOnly={readOnly}
            disabled={readOnly}
            onChange={(v) => setField("finishPeriod", v)}
            onBlur={handleBlur}
          />
        </MiniField>
        <MiniField label="Duration" required>
          <NumberBox value={visit.periodDuration} readOnly maxLength={3} />
        </MiniField>
      </div>
      <div className="gvisit-mini-grid gvisit-mini-grid--minmax">
        <MiniField label="Min">
          <SignedNumberBox
            sign="-"
            value={visit.tolerencePeriod}
            disabled={readOnly}
            onChange={(v) => setField("tolerencePeriod", v)}
          />
        </MiniField>
        <MiniField label="Max">
          <SignedNumberBox
            sign="+"
            value={visit.upperTolerenceLimit}
            disabled={readOnly}
            onChange={(v) => setField("upperTolerenceLimit", v)}
          />
        </MiniField>
      </div>
    </>
  );
}

function NonResidentialDetailForm({ visit, onChange, readOnly = false }) {
  const setField = (field, value) => {
    if (readOnly) return;
    onChange({ ...visit, [field]: value });
  };

  return (
    <div className="gvisit-mini-grid gvisit-mini-grid--3">
      <MiniField label="Day" required>
        <NumberBox
          value={visit.nonResidVisitOccur}
          maxLength={3}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(v) => setField("nonResidVisitOccur", v)}
        />
      </MiniField>
      <MiniField label="Min">
        <SignedNumberBox
          sign="-"
          value={visit.nonResidTolerencePeriod}
          disabled={readOnly}
          onChange={(v) => setField("nonResidTolerencePeriod", v)}
        />
      </MiniField>
      <MiniField label="Max">
        <SignedNumberBox
          sign="+"
          value={visit.nonResidUpperTolerenceLimit}
          disabled={readOnly}
          onChange={(v) => setField("nonResidUpperTolerenceLimit", v)}
        />
      </MiniField>
    </div>
  );
}

function ReferenceVisitSelect({ visit, allVisits, onChange, readOnly = false }) {
  const { show, options } = useMemo(
    () => buildReferenceVisitOptions(allVisits, visit),
    [allVisits, visit],
  );

  const optionValues = options.map((option) => option.value).join("|");

  useEffect(() => {
    if (readOnly || !show || !options.length || !visit) return;
    const current = String(visit.refVisitId || "").trim();
    if (current && options.some((option) => option.value === current)) return;
    const defaultValue = options[options.length - 1]?.value;
    if (!defaultValue) return;
    onChange({ ...visit, refVisitId: defaultValue });
  }, [show, visit?.id, visit?.visitsPerStudyId, optionValues, visit, onChange, readOnly]);

  if (!show) return null;

  const value = String(visit?.refVisitId || "");

  return (
    <div className="gvisit-ref-visit">
      <LegacyField label="Select Reference Visit" required compact>
        <select
          className="gvisit-select-box"
          value={value}
          disabled={readOnly}
          onChange={(event) =>
            onChange({ ...visit, refVisitId: event.target.value })
          }
        >
          <option value="">Select reference visit</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </LegacyField>
    </div>
  );
}

function VisitTimePointSection({
  visit,
  allVisits = [],
  onChange,
  onSave,
  saving,
  onNotify,
  saveLabel = "Save",
  readOnly = false,
}) {
  const isResidential = visit.visitType === VISIT_TYPE.RESIDENTIAL;
  const [rangeError, setRangeError] = useState("");

  const setType = (visitType) => {
    if (readOnly) return;
    setRangeError("");
    onChange(
      createVisitDraft({
        ...visit,
        visitType,
        isScreening: false,
        locked: false,
      }),
    );
  };

  const handleSave = () => {
    const refCheck = validateVisitReferenceForSave(visit, allVisits);
    if (!refCheck.valid) {
      onNotify?.({ message: refCheck.message, type: "warning" });
      return;
    }
    if (isResidential) {
      const error = getResidentialRangeError(visit.startPeriod, visit.finishPeriod, {
        requireBoth: true,
      });
      if (error) {
        setRangeError(error);
        onNotify?.({ message: error, type: "warning" });
        return;
      }
    }
    setRangeError("");
    onSave?.();
  };

  return (
    <StaticSetupSection
      title="Visit time point"
      headerExtra={(
        <div className="gvisit-type-switch gvisit-type-switch--compact" role="group" aria-label="Visit type">
          <button
            type="button"
            className={`gvisit-type-switch__btn${
              !isResidential ? " is-active" : ""
            }`}
            disabled={readOnly}
            onClick={() => setType(VISIT_TYPE.NON_RESIDENTIAL)}
          >
            Non-res
          </button>
          <button
            type="button"
            className={`gvisit-type-switch__btn${
              isResidential ? " is-active" : ""
            }`}
            disabled={readOnly}
            onClick={() => setType(VISIT_TYPE.RESIDENTIAL)}
          >
            Residential
          </button>
        </div>
      )}
      onSave={readOnly ? null : handleSave}
      saving={saving}
      saveLabel={saveLabel}
      readOnly={readOnly}
    >
      <ReferenceVisitSelect
        visit={visit}
        allVisits={allVisits}
        onChange={onChange}
        readOnly={readOnly}
      />
      {isResidential ? (
        <>
          {rangeError ? (
            <p className="gvisit-timepoint-range-error">{rangeError}</p>
          ) : null}
          <ResidentialDetailForm
            visit={visit}
            onChange={onChange}
            onNotify={onNotify}
            readOnly={readOnly}
          />
        </>
      ) : (
        <NonResidentialDetailForm visit={visit} onChange={onChange} readOnly={readOnly} />
      )}
    </StaticSetupSection>
  );
}

function VisitDetailPanel({
  visit,
  allVisits = [],
  setupFlags,
  visitRailSlot = null,
  onChange,
  optionalSetupProps = {},
  onSaveVisitDetails,
  savingVisitKey = null,
}) {
  const isAdhoc = visit ? isAdhocVisit(visit) : false;
  const isScreening =
    visit && !isAdhoc && (visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening);
  const visitSaveKey = visit?.id || visit?.visitsPerStudyId;
  const visitIdentityKey = String(visitSaveKey || "");
  const isSaving = savingVisitKey === visitSaveKey;
  const [timePointSaved, setTimePointSaved] = useState(false);
  const savedBaselineRef = useRef(null);
  const persistedTimePointRef = useRef({});
  const baselineByVisitRef = useRef({});

  useEffect(() => {
    if (!visit || !visitIdentityKey) {
      savedBaselineRef.current = null;
      setTimePointSaved(false);
      return;
    }
    if (baselineByVisitRef.current[visitIdentityKey] === undefined) {
      baselineByVisitRef.current[visitIdentityKey] = createVisitDraft(visit);
      persistedTimePointRef.current[visitIdentityKey] =
        visitHasPersistableDetails(visit, { screening: isScreening });
    }
    savedBaselineRef.current = baselineByVisitRef.current[visitIdentityKey];
    setTimePointSaved(!!persistedTimePointRef.current[visitIdentityKey]);
  }, [visit, visitIdentityKey, isScreening]);

  const saveSection = async (section) => {
    if (!visit) return;
    try {
      await onSaveVisitDetails?.({
        visit,
        section,
        priorVisit: savedBaselineRef.current,
      });
      if (section === "time_point" || section === "screening") {
        persistedTimePointRef.current[visitIdentityKey] = true;
        setTimePointSaved(true);
      }
      const nextBaseline = createVisitDraft(visit);
      baselineByVisitRef.current[visitIdentityKey] = nextBaseline;
      savedBaselineRef.current = nextBaseline;
    } catch {
      // Parent shows error alert.
    }
  };

  const timePointSaveLabel = timePointSaved ? "Update" : "Save";
  const timePointReadOnly =
    !!optionalSetupProps.isStudyArchived ||
    (!!optionalSetupProps.isScheduleLive && timePointSaved);

  const timePointSlot =
    visit && !isAdhoc
      ? isScreening
        ? (
            <ScreeningDetailForm
              visit={visit}
              onChange={onChange}
              onSave={() => saveSection("screening")}
              saving={isSaving}
              saveLabel={timePointSaveLabel}
              onNotify={optionalSetupProps.onNotify}
              readOnly={timePointReadOnly}
            />
          )
        : (
            <VisitTimePointSection
              visit={visit}
              allVisits={allVisits}
              onChange={onChange}
              onSave={() => saveSection("time_point")}
              saving={isSaving}
              saveLabel={timePointSaveLabel}
              onNotify={optionalSetupProps.onNotify}
              readOnly={timePointReadOnly}
            />
          )
      : null;

  return (
    <ScheduleVisitOptionalSections
      visit={visit}
      setupFlags={setupFlags}
      compact
      layout="workspace"
      visitRailSlot={visitRailSlot}
      timePointSlot={timePointSlot}
      {...optionalSetupProps}
    />
  );
}

function CohortVisitLayout({
  cohorts,
  studyScreening,
  setupFlags,
  onCohortVisitsChange,
  optionalSetupProps = {},
  onAddVisit,
  onSaveVisitDetails,
  onRequestRemoveVisit,
  onReorderVisit,
  savingVisitKey = null,
  structureBusy = false,
  structureLocked = false,
  addVisitLocked = false,
  scheduleReadOnly = false,
  isStudyArchived = false,
  allGroupsSameSchedule = true,
  groupManageProps = null,
}) {
  const dropdownCohorts = useMemo(() => cohortsForGroupDropdown(cohorts), [cohorts]);

  const [activeCohortId, setActiveCohortId] = useState(dropdownCohorts[0]?.id || null);
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [renamingVisitId, setRenamingVisitId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameOriginalName, setRenameOriginalName] = useState("");
  const [pendingSelectDbId, setPendingSelectDbId] = useState(null);

  const [pendingSelectGroupId, setPendingSelectGroupId] = useState(null);

  useEffect(() => {
    if (dropdownCohorts.some((cohort) => cohort.id === activeCohortId)) {
      if (pendingSelectGroupId) setPendingSelectGroupId(null);
      return;
    }
    const preferred =
      pendingSelectGroupId &&
      dropdownCohorts.some((cohort) => cohort.id === pendingSelectGroupId)
        ? pendingSelectGroupId
        : dropdownCohorts[0]?.id || null;
    setActiveCohortId(preferred);
    setSelectedVisitId(null);
    setPendingSelectGroupId(null);
  }, [activeCohortId, dropdownCohorts, pendingSelectGroupId]);

  const activeCohort =
    dropdownCohorts.find((c) => c.id === activeCohortId) || dropdownCohorts[0];
  const visits = activeCohort?.visits || [];
  const cohortScreening = !!activeCohort?.isStudyScreeningGroup;
  const list = useMemo(
    () => visitsForVisitRail(visits),
    [visits],
  );
  const persistableVisits = useMemo(() => visitsWithoutAdhoc(list), [list]);

  useEffect(() => {
    if (!list.some((v) => sameVisitId(v.id, selectedVisitId))) {
      setSelectedVisitId(list[0]?.id || null);
    }
  }, [list, selectedVisitId]);

  useEffect(() => {
    if (!pendingSelectDbId) return;
    const match = list.find(
      (visit) => String(visit.visitsPerStudyId) === String(pendingSelectDbId),
    );
    if (match) {
      setSelectedVisitId(match.id);
      setPendingSelectDbId(null);
    }
  }, [list, pendingSelectDbId]);

  const selectedVisit =
    list.find((v) => sameVisitId(v.id, selectedVisitId)) || list[0];

  const handleChange = (nextList) => {
    onCohortVisitsChange?.(activeCohort.id, nextList);
  };

  const updateVisitById = (id, next) => {
    handleChange(list.map((v) => (v.id === id ? next : v)));
  };

  const removeAt = (visit) => {
    if (structureLocked) return;
    if (!canDeleteVisitFromList(visit, persistableVisits, cohortScreening)) return;
    onRequestRemoveVisit?.({
      visit,
      groupId: activeCohort?.id,
    });
  };

  const handleReorder = ({ visit, fromIndex, toIndex }) => {
    if (structureLocked) return;
    onReorderVisit?.({
      visit,
      fromIndex,
      toIndex,
      groupId: activeCohort?.id,
    });
  };

  const addVisit = async () => {
    if (
      cohortScreening ||
      persistableVisits.length >= 99 ||
      structureBusy ||
      addVisitLocked ||
      !onAddVisit
    ) {
      return;
    }
    const visitName = nextDefaultVisitName(persistableVisits);
    const referenceVisit =
      selectedVisit && !isAdhocVisit(selectedVisit) ? selectedVisit : persistableVisits.at(-1);
    const result = await onAddVisit({
      referenceVisit,
      visitName,
      groupId: activeCohort?.id,
      isStudyScreeningGroup: cohortScreening,
    });
    if (result?.visitsPerStudyId) {
      setPendingSelectDbId(result.visitsPerStudyId);
    }
  };

  const commitRename = async () => {
    const trimmed = String(renameValue || "").trim();
    const current = list.find((v) => v.id === renamingVisitId);
    if (!current || !trimmed) return;
    if (visitNameUnchanged(current, trimmed, renameOriginalName)) {
      setRenamingVisitId(null);
      setRenameOriginalName("");
      return;
    }
    if (findDuplicateVisitName(list, current, trimmed)) {
      optionalSetupProps.onNotify?.({
        message: "A visit with this name already exists in this schedule.",
        type: "warning",
      });
      return;
    }
    if (!current.visitsPerStudyId) {
      optionalSetupProps.onNotify?.({
        message: "This visit is not saved yet. Add the visit first.",
        type: "warning",
      });
      return;
    }
    const prevName = current.visitName;
    const next = { ...current, visitName: trimmed };
    updateVisitById(current.id, next);
    try {
      await onSaveVisitDetails?.({
        visit: next,
        section: "visit_name",
        groupId: activeCohort?.id,
      });
      setRenamingVisitId(null);
      setRenameOriginalName("");
    } catch {
      updateVisitById(current.id, { ...current, visitName: prevName });
    }
  };

  const startRename = (visit) => {
    if (structureLocked) return;
    setRenamingVisitId(visit.id);
    setRenameValue(visit.visitName || "");
    setRenameOriginalName(visit.visitName || "");
  };

  const cancelRename = () => {
    setRenamingVisitId(null);
    setRenameOriginalName("");
  };

  const canManageTreatmentGroups = !!groupManageProps && !groupManageProps.disabled;
  const showGroupActions =
    canManageTreatmentGroups &&
    !!activeCohort?.isTreatmentGroup &&
    !activeCohort?.isStudyScreeningGroup;

  const handleDeleteGroup = async (groupId, groupName) => {
    const idx = dropdownCohorts.findIndex((cohort) => String(cohort.id) === String(groupId));
    const preferredNext =
      idx >= 0
        ? dropdownCohorts[idx + 1]?.id ?? dropdownCohorts[idx - 1]?.id ?? null
        : null;
    if (preferredNext) setPendingSelectGroupId(preferredNext);
    await groupManageProps?.onDeleteGroup?.(groupId, groupName);
  };

  return (
    <VisitDetailPanel
      visit={selectedVisit}
      allVisits={list}
      setupFlags={setupFlags}
      visitRailSlot={
        <>
          {dropdownCohorts.length || groupManageProps ? (
            <ScheduleGroupSelector
              options={dropdownCohorts}
              value={activeCohort?.id || ""}
              onChange={(nextId) => {
                setActiveCohortId(nextId);
                setSelectedVisitId(null);
                cancelRename();
              }}
              allGroupsSameSchedule={allGroupsSameSchedule}
              onSameScheduleChange={groupManageProps?.onSameScheduleChange}
              showAdd={canManageTreatmentGroups}
              onAdd={groupManageProps?.onAdd}
              selectedCohort={activeCohort}
              showGroupActions={showGroupActions}
              onSaveGroupName={groupManageProps?.onSaveGroupName}
              onDeleteGroup={handleDeleteGroup}
              busy={groupManageProps?.busy}
              disabled={groupManageProps?.disabled}
            />
          ) : null}
          <div className="gvisit-rail__list">
            <VisitRailList
              visits={list}
              persistableVisits={persistableVisits}
              studyScreening={cohortScreening}
              selectedVisitId={selectedVisitId}
              renamingVisitId={renamingVisitId}
              renameValue={renameValue}
              structureBusy={structureBusy}
              lockVisitIdentity={structureLocked}
              onRenameValueChange={setRenameValue}
              onRenameStart={startRename}
              onRenameCommit={commitRename}
              onRenameCancel={cancelRename}
              onSelect={(visit) => {
                cancelRename();
                setSelectedVisitId(visit.id);
              }}
              onRequestRemove={removeAt}
              onReorder={handleReorder}
            />
          </div>
          {!cohortScreening ? (
            <button
              type="button"
              className="gvisit-rail-add"
              onClick={addVisit}
              disabled={structureBusy || addVisitLocked || persistableVisits.length >= 99}
            >
              {structureBusy ? "Adding…" : "+ Add visit"}
            </button>
          ) : null}
        </>
      }
      optionalSetupProps={{
        ...optionalSetupProps,
        groupId: resolveScheduleGroupId({
          groupId: activeCohort?.id,
          cohorts,
        }),
      }}
      onSaveVisitDetails={(payload) =>
        onSaveVisitDetails?.({ ...payload, groupId: activeCohort?.id })
      }
      savingVisitKey={savingVisitKey}
      onChange={(next) => updateVisitById(selectedVisit?.id, next)}
    />
  );
}

export default function ScheduleVisitBuilder({
  mode = "single",
  visits = [],
  onVisitsChange,
  cohorts = [],
  onCohortVisitsChange,
  studyScreening = false,
  setupFlags = {},
  studyScheduleId = null,
  trialStudyId = null,
  organizationId = null,
  createdBy = null,
  screeningRandomisationTogether = false,
  onNotify = null,
  onAuditEvent = null,
  onAuditLogRefresh = null,
  onReadinessRefresh = null,
  onVisitPlannerSnapshotPatch = null,
  onPromptAuditedVisitAction = null,
  onAddVisit = null,
  onSaveVisitDetails = null,
  onRequestRemoveVisit = null,
  onReorderVisit = null,
  savingVisitKey = null,
  structureBusy = false,
  structureLocked = false,
  addVisitLocked = false,
  scheduleReadOnly = false,
  isScheduleLive = false,
  isStudyArchived = false,
  allGroupsSameSchedule = true,
  groupManageProps = null,
}) {
  const defaultGroupId = useMemo(
    () => resolveScheduleGroupId({ cohorts }),
    [cohorts],
  );

  // onAuditLogRefresh is passed from ScheduleEditor (loadAuditLog).
  const optionalSetupProps = useMemo(
    () => ({
      studyScheduleId,
      trialStudyId,
      organizationId,
      createdBy,
      screeningRandomisationTogether,
      onNotify,
      onAuditEvent,
      onAuditLogRefresh,
      onReadinessRefresh,
      onVisitPlannerSnapshotPatch,
      onPromptAuditedVisitAction,
      groupId: defaultGroupId,
      readOnly: scheduleReadOnly,
      isScheduleLive,
      isStudyArchived,
    }),
    [
      studyScheduleId,
      trialStudyId,
      organizationId,
      createdBy,
      screeningRandomisationTogether,
      onNotify,
      onAuditEvent,
      onAuditLogRefresh,
      onReadinessRefresh,
      onVisitPlannerSnapshotPatch,
      onPromptAuditedVisitAction,
      defaultGroupId,
      scheduleReadOnly,
      isScheduleLive,
      isStudyArchived,
    ],
  );
  const railVisits = useMemo(() => visitsForVisitRail(visits), [visits]);

  const [selectedVisitId, setSelectedVisitId] = useState(
    () => railVisits[0]?.id || null,
  );
  const [renamingVisitId, setRenamingVisitId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameOriginalName, setRenameOriginalName] = useState("");
  const [pendingSelectDbId, setPendingSelectDbId] = useState(null);
  const persistableVisits = useMemo(
    () => visitsWithoutAdhoc(railVisits),
    [railVisits],
  );

  useEffect(() => {
    if (!railVisits.some((v) => sameVisitId(v.id, selectedVisitId))) {
      setSelectedVisitId(railVisits[0]?.id || null);
    }
  }, [railVisits, selectedVisitId]);

  useEffect(() => {
    if (!pendingSelectDbId) return;
    const match = railVisits.find(
      (visit) => String(visit.visitsPerStudyId) === String(pendingSelectDbId),
    );
    if (match) {
      setSelectedVisitId(match.id);
      setPendingSelectDbId(null);
    }
  }, [railVisits, pendingSelectDbId]);

  const selectedVisit =
    railVisits.find((v) => sameVisitId(v.id, selectedVisitId)) || railVisits[0];

  const updateVisitById = (id, next) => {
    onVisitsChange(railVisits.map((v) => (v.id === id ? next : v)));
  };

  const removeVisit = (visit) => {
    if (structureLocked) return;
    if (!canDeleteVisitFromList(visit, persistableVisits, studyScreening)) return;
    onRequestRemoveVisit?.({ visit });
  };

  const handleReorder = ({ visit, fromIndex, toIndex }) => {
    if (structureLocked) return;
    onReorderVisit?.({ visit, fromIndex, toIndex });
  };

  const startRename = (visit) => {
    if (structureLocked) return;
    setRenamingVisitId(visit.id);
    setRenameValue(visit.visitName || "");
    setRenameOriginalName(visit.visitName || "");
  };

  const cancelRename = () => {
    setRenamingVisitId(null);
    setRenameOriginalName("");
  };

  const addVisit = async () => {
    if (persistableVisits.length >= 99 || structureBusy || addVisitLocked || !onAddVisit) {
      return;
    }
    const visitName = nextDefaultVisitName(persistableVisits);
    const referenceVisit =
      selectedVisit && !isAdhocVisit(selectedVisit) ? selectedVisit : persistableVisits.at(-1);
    const result = await onAddVisit({
      referenceVisit,
      visitName,
    });
    if (result?.visitsPerStudyId) {
      setPendingSelectDbId(result.visitsPerStudyId);
    }
  };

  const commitRename = async () => {
    const trimmed = String(renameValue || "").trim();
    const current = railVisits.find((v) => v.id === renamingVisitId);
    if (!current || !trimmed) return;
    if (visitNameUnchanged(current, trimmed, renameOriginalName)) {
      setRenamingVisitId(null);
      setRenameOriginalName("");
      return;
    }
    if (findDuplicateVisitName(railVisits, current, trimmed)) {
      onNotify?.({
        message: "A visit with this name already exists in this schedule.",
        type: "warning",
      });
      return;
    }
    if (!current.visitsPerStudyId) {
      onNotify?.({
        message: "This visit is not saved yet. Add the visit first.",
        type: "warning",
      });
      return;
    }
    const prevName = current.visitName;
    const next = { ...current, visitName: trimmed };
    updateVisitById(current.id, next);
    try {
      await onSaveVisitDetails?.({ visit: next, section: "visit_name" });
      setRenamingVisitId(null);
      setRenameOriginalName("");
    } catch {
      updateVisitById(current.id, { ...current, visitName: prevName });
    }
  };

  if (mode === "cohorts") {
    return (
      <CohortVisitLayout
        cohorts={cohorts}
        studyScreening={studyScreening}
        setupFlags={setupFlags}
        onCohortVisitsChange={onCohortVisitsChange}
        optionalSetupProps={optionalSetupProps}
        onAddVisit={onAddVisit}
        onSaveVisitDetails={onSaveVisitDetails}
        onRequestRemoveVisit={onRequestRemoveVisit}
        onReorderVisit={onReorderVisit}
        savingVisitKey={savingVisitKey}
        structureBusy={structureBusy}
        structureLocked={structureLocked}
        addVisitLocked={addVisitLocked}
        scheduleReadOnly={scheduleReadOnly}
        isStudyArchived={isStudyArchived}
        allGroupsSameSchedule={allGroupsSameSchedule}
        groupManageProps={groupManageProps}
      />
    );
  }

  return (
    <VisitDetailPanel
      visit={selectedVisit}
      allVisits={railVisits}
      setupFlags={setupFlags}
      visitRailSlot={
        <>
          <div className="gvisit-rail__list">
            <VisitRailList
              visits={railVisits}
              persistableVisits={persistableVisits}
              studyScreening={studyScreening}
              selectedVisitId={selectedVisitId}
              renamingVisitId={renamingVisitId}
              renameValue={renameValue}
              structureBusy={structureBusy}
              lockVisitIdentity={structureLocked}
              onRenameValueChange={setRenameValue}
              onRenameStart={startRename}
              onRenameCommit={commitRename}
              onRenameCancel={cancelRename}
              onSelect={(visit) => {
                cancelRename();
                setSelectedVisitId(visit.id);
              }}
              onRequestRemove={removeVisit}
              onReorder={handleReorder}
            />
          </div>
          <button
            type="button"
            className="gvisit-rail-add"
            disabled={structureBusy || addVisitLocked || persistableVisits.length >= 99}
            onClick={addVisit}
          >
            {structureBusy ? "Adding…" : "+ Add visit"}
          </button>
        </>
      }
      optionalSetupProps={optionalSetupProps}
      onSaveVisitDetails={onSaveVisitDetails}
      savingVisitKey={savingVisitKey}
      onChange={(next) => updateVisitById(selectedVisit?.id, next)}
    />
  );
}
